import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef } from 'react';
import { Platform, Pressable, ScrollView } from 'react-native';

import { useThemePalette } from '@/config/theme';

import { AppText } from './AppText';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional leading icon (Ionicons). Pills without one stay text-only. */
  icon?: keyof typeof Ionicons.glyphMap;
}

/**
 * Horizontal pill switcher used for in-screen tabs. Scrolls when the pills
 * overflow; the active pill is a translucent cyan-glass fill with a hairline
 * border and a soft glow (web), and is auto-scrolled into view so a tab that
 * sits off-screen is never hidden. Both states keep a border so heights match.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  const palette = useThemePalette();
  const scrollRef = useRef<ScrollView>(null);
  const layouts = useRef<Record<string, number>>({});

  // Bring the active pill into view when it changes (e.g. a deep-linked tab).
  useEffect(() => {
    const x = layouts.current[value];
    if (x != null) scrollRef.current?.scrollTo({ x: Math.max(0, x - 12), animated: true });
  }, [value]);

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ paddingHorizontal: 16, gap: 10, paddingVertical: 4 }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            onPress={() => onChange(option.value)}
            onLayout={(e) => {
              layouts.current[option.value] = e.nativeEvent.layout.x;
            }}
            className={`flex-row items-center gap-1.5 rounded-full border px-4 py-2.5 ${
              selected ? 'border-volt-line bg-volt-glass-strong' : 'border-line bg-inset'
            }`}
            style={
              selected && Platform.OS === 'web'
                ? { boxShadow: '0 0 20px rgba(34, 211, 255, 0.22)' }
                : undefined
            }
          >
            {option.icon ? (
              <Ionicons
                name={option.icon}
                size={14}
                color={selected ? palette.voltBright : palette.inactive}
              />
            ) : null}
            <AppText
              variant="micro"
              weight="semibold"
              numberOfLines={1}
              className={selected ? 'text-volt-bright' : 'text-muted'}
            >
              {option.label}
            </AppText>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
